import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
    Add,
    Edit2,
    Eye,
    EyeSlash,
    Maximize4,
    Refresh,
    Setting4,
    Spinner,
    TextalignCenter,
    TextalignLeft,
    TextalignRight,
    Trash,
} from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { useBrandLogo } from '@/hooks/use-brand-logo';
import { useConfirmDelete } from '@/hooks/use-confirm-delete';
import { useHoverIntent } from '@/hooks/use-hover-intent';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { captureFeatureBlocked } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import { IMAGE_UPLOAD_ACCEPT } from '@/utils/validate-image-file';
import type { Alignments, HeaderCustomizations } from './types';
import type { DragEvent, ReactNode } from 'react';

const LOGO_SIZES = [
    { value: 'small', label: 'P', title: 'Pequena' },
    { value: 'medium', label: 'M', title: 'Média' },
    { value: 'large', label: 'G', title: 'Grande' },
] as const;

const ALIGNMENTS = [
    { value: 'left', title: 'Esquerda', Icon: TextalignLeft },
    { value: 'center', title: 'Centro', Icon: TextalignCenter },
    { value: 'right', title: 'Direita', Icon: TextalignRight },
] as const;

/** Where the out-of-flow slot parks itself, following the header's alignment. */
const SLOT_POSITION: Record<Alignments, string> = {
    left: 'left-0',
    center: '-translate-x-1/2 left-1/2',
    right: 'right-0',
};

const SLOT_CLASS =
    'h-auto gap-1.5 rounded-md border-dashed border-border-strong px-2.5 py-1 text-xs font-medium text-muted-foreground hover:border-primary hover:text-primary [&_svg]:size-3.5';

const SEGMENT_CLASS = 'size-5 rounded p-0 text-xs font-semibold [&_svg]:size-3';

const ROW_CLASS = 'h-auto w-full justify-start gap-2.5 rounded-md px-2 py-1.5 text-sm font-normal';

/** A menu line that only hosts a control — a button here would nest buttons. */
const StaticRow = ({
    icon,
    label,
    children,
}: {
    icon: ReactNode;
    label: string;
    children: ReactNode;
}) => (
    <div className={cn(ROW_CLASS, 'flex items-center')}>
        <span className="flex-none text-muted-foreground">{icon}</span>
        <span className="flex-1">{label}</span>
        <span className="flex gap-0.5 rounded-md border border-border p-0.5">{children}</span>
    </div>
);

interface HeaderLogoControlProps {
    /** The header's `<img>`, or null when no logo is on the paper right now. */
    children: ReactNode;
    /** True when the brand asset exists, even if this laudo hides it. */
    hasLogo: boolean;
    alignment: Alignments;
    logoSize: NonNullable<HeaderCustomizations['logo_size']>;
}

/**
 * Editor-only chrome around the header logo: click it to trocar, redimensionar,
 * ocultar or remover without going through Personalizar.
 *
 * Two layout rules keep pagination honest, because `HiddenReportMeasurer`
 * renders the same header *without* this wrapper:
 *  - with a logo, the wrapper is a bare `flex w-fit` box around the untouched
 *    `<img>`, and every affordance (ring, pencil, popover) is outline- or
 *    absolutely-positioned, so it adds no height;
 *  - with no logo on the paper, the whole control is absolute — not a flex
 *    item, so it does not even draw the parent's `gap-2` — and sits in the
 *    page's top margin.
 */
export function HeaderLogoControl({
    children,
    hasLogo,
    alignment,
    logoSize,
}: HeaderLogoControlProps) {
    const { formData, handleUpdate, setShowCustomizeModal } = useReportWizard();
    const confirmDelete = useConfirmDelete();
    const inputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    // The "editar logo" badge waits for hover intent like the rest of the
    // report chrome; an open popover pins it regardless of the pointer.
    const logoHover = useHoverIntent({ keepOpen: open });

    const updateHeader = useCallback(
        (updates: Partial<HeaderCustomizations>) => {
            handleUpdate((prev) => ({
                customizations: {
                    ...prev.customizations,
                    header: {
                        alignment: 'left',
                        ...prev.customizations?.header,
                        ...updates,
                    },
                },
            }));
        },
        [handleUpdate],
    );

    const showLogo = useCallback(() => updateHeader({ show_logo: true }), [updateHeader]);

    const { logoAccess, uploadState, uploadLogo, removeLogo } = useBrandLogo({
        data: formData,
        onUpdate: handleUpdate,
        surface: 'report_editor',
        // A fresh upload is meant to be seen, even if this laudo had it hidden.
        onUploaded: showLogo,
    });

    const isUploading = uploadState.status === 'uploading';

    // There is no room on the paper for an inline error, and the upload can
    // genuinely fail (corrupt file, backend rejection).
    useEffect(() => {
        if (uploadState.status === 'error' && uploadState.error) toast.error(uploadState.error);
    }, [uploadState]);

    const pickFile = useCallback(() => {
        if (logoAccess.shouldBlock) {
            captureFeatureBlocked('report_editor', logoAccess.decision);
            toast.warning(getEntitlementBlockedMessage(logoAccess.decision));
            return;
        }

        inputRef.current?.click();
    }, [logoAccess.shouldBlock, logoAccess.decision]);

    const handleRemove = useCallback(() => {
        confirmDelete({
            title: 'Remover a logo?',
            description: 'Ela sai deste cabeçalho e também da capa e do rodapé dos seus laudos.',
            confirmText: 'Remover',
            onConfirm: (close) => {
                removeLogo();
                close();
            },
        });
    }, [confirmDelete, removeLogo]);

    const isFileDrag = (event: DragEvent) =>
        Array.from(event.dataTransfer?.types ?? []).includes('Files');

    /** The page turns a dropped image into a bloco; over the logo it is a logo. */
    const handleDrop = useCallback(
        (event: DragEvent) => {
            if (!isFileDrag(event)) return;

            event.preventDefault();
            event.stopPropagation();

            const file = event.dataTransfer.files?.[0];
            if (file) void uploadLogo(file);
        },
        [uploadLogo],
    );

    const handleDragOver = useCallback((event: DragEvent) => {
        if (!isFileDrag(event)) return;

        event.preventDefault();
        event.stopPropagation();
    }, []);

    // Deliberately outside the popover: picking a file blurs the menu, and an
    // input unmounted with it would never fire its own change event.
    const fileInput = (
        <Input
            ref={inputRef}
            type="file"
            accept={IMAGE_UPLOAD_ACCEPT}
            className="hidden"
            data-testid="header-logo-input"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0];
                if (file) void uploadLogo(file);
                event.target.value = '';
            }}
        />
    );

    // No asset at all: one dashed slot, one action. A menu here would only
    // offer rows that need a logo to mean anything.
    if (!hasLogo) {
        return (
            <div
                className={cn('absolute bottom-full mb-2 print:hidden', SLOT_POSITION[alignment])}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                <Button
                    type="button"
                    variant="surface"
                    onClick={pickFile}
                    disabled={isUploading}
                    data-testid="header-logo-add"
                    className={SLOT_CLASS}
                >
                    {isUploading ? <Spinner className="animate-spin" /> : <Add />}
                    {isUploading ? `Enviando… ${uploadState.progress}%` : 'Adicionar a sua logo'}
                </Button>
                {fileInput}
            </div>
        );
    }

    const menu = (
        <PopoverContent
            align={alignment === 'right' ? 'end' : alignment === 'center' ? 'center' : 'start'}
            sideOffset={10}
            className="w-64 p-1"
            data-testid="header-logo-menu"
            // The menu opens over the paper; stealing focus into it would tear
            // the caret out of whatever the psicóloga was writing.
            onOpenAutoFocus={(event) => event.preventDefault()}
        >
            <Button
                type="button"
                variant="ghost"
                onClick={pickFile}
                disabled={isUploading}
                data-testid="header-logo-replace"
                className={ROW_CLASS}
            >
                <span className="flex-none text-muted-foreground">
                    {isUploading ? <Spinner className="animate-spin" /> : <Refresh />}
                </span>
                <span className="flex-1 text-left">
                    {isUploading ? `Enviando… ${uploadState.progress}%` : 'Trocar a logo…'}
                </span>
            </Button>

            <StaticRow icon={<Maximize4 className="size-4" />} label="Tamanho">
                {LOGO_SIZES.map((size) => (
                    <Button
                        key={size.value}
                        type="button"
                        variant="ghost"
                        title={size.title}
                        data-testid={`header-logo-size-${size.value}`}
                        onClick={() => updateHeader({ logo_size: size.value })}
                        className={cn(
                            SEGMENT_CLASS,
                            logoSize === size.value
                                ? 'hover:bg-primary/92 bg-primary text-primary-foreground hover:text-primary-foreground'
                                : 'text-muted-foreground',
                        )}
                    >
                        {size.label}
                    </Button>
                ))}
            </StaticRow>

            <StaticRow icon={<TextalignLeft className="size-4" />} label="Alinhar cabeçalho">
                {ALIGNMENTS.map(({ value, title, Icon }) => (
                    <Button
                        key={value}
                        type="button"
                        variant="ghost"
                        title={title}
                        aria-label={title}
                        data-testid={`header-logo-align-${value}`}
                        onClick={() => updateHeader({ alignment: value })}
                        className={cn(
                            SEGMENT_CLASS,
                            alignment === value
                                ? 'hover:bg-primary/92 bg-primary text-primary-foreground hover:text-primary-foreground'
                                : 'text-muted-foreground',
                        )}
                    >
                        <Icon />
                    </Button>
                ))}
            </StaticRow>

            <div className="my-1 h-px bg-border" />

            <Button
                type="button"
                variant="ghost"
                data-testid="header-logo-visibility"
                className={ROW_CLASS}
                onClick={() => {
                    if (children) updateHeader({ show_logo: false });
                    else showLogo();
                    setOpen(false);
                }}
            >
                <span className="flex-none text-muted-foreground">
                    {children ? <EyeSlash /> : <Eye />}
                </span>
                <span className="flex-1 text-left">
                    {children ? 'Ocultar neste laudo' : 'Mostrar neste laudo'}
                </span>
            </Button>

            <Button
                type="button"
                variant="ghostDestructive"
                data-testid="header-logo-remove"
                className={cn(ROW_CLASS, 'text-destructive')}
                onClick={() => {
                    setOpen(false);
                    handleRemove();
                }}
            >
                <span className="flex-none">
                    <Trash />
                </span>
                <span className="flex-1 text-left">Remover</span>
            </Button>

            <div className="my-1 h-px bg-border" />

            <Button
                type="button"
                variant="ghost"
                data-testid="header-logo-more"
                className={ROW_CLASS}
                onClick={() => {
                    setOpen(false);
                    setShowCustomizeModal(true, 'brand');
                }}
            >
                <span className="flex-none text-muted-foreground">
                    <Setting4 />
                </span>
                <span className="flex-1 text-left">Mais opções de logo…</span>
            </Button>
        </PopoverContent>
    );

    // Hidden in this laudo, but the asset is there: the slot is the only way
    // back, so it says so instead of offering to add a logo again.
    if (!children) {
        return (
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverAnchor asChild>
                    <div
                        className={cn(
                            'absolute bottom-full mb-2 print:hidden',
                            SLOT_POSITION[alignment],
                        )}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <Button
                            type="button"
                            variant="surface"
                            onClick={() => setOpen((prev) => !prev)}
                            data-testid="header-logo-hidden"
                            className={SLOT_CLASS}
                        >
                            <EyeSlash />
                            Logo oculta
                        </Button>
                        {fileInput}
                    </div>
                </PopoverAnchor>
                {menu}
            </Popover>
        );
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverAnchor asChild>
                <span
                    {...logoHover.hoverProps}
                    {...logoHover.dataProps}
                    className="group/logo relative flex w-fit"
                >
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setOpen((prev) => !prev)}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        aria-label="Editar logo"
                        data-testid="header-logo-trigger"
                        className={cn(
                            'size-auto rounded-sm p-0 outline-1 outline-offset-4 outline-transparent transition-colors hover:bg-transparent',
                            'hover:outline-dashed hover:outline-primary/50',
                            open && 'outline-dashed outline-primary/50',
                        )}
                    >
                        {children}
                    </Button>

                    <span
                        aria-hidden="true"
                        className={cn(
                            'pointer-events-none absolute -right-2.5 -top-2.5 flex size-5 items-center justify-center rounded-full border border-border bg-card text-primary shadow-sm transition-opacity',
                            open
                                ? 'opacity-100'
                                : 'opacity-0 group-data-[hover-intent=on]/logo:opacity-100',
                        )}
                    >
                        {isUploading ? (
                            <Spinner className="size-3 animate-spin" />
                        ) : (
                            <Edit2 className="size-3" />
                        )}
                    </span>
                    {fileInput}
                </span>
            </PopoverAnchor>
            {menu}
        </Popover>
    );
}
